import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import axios from 'axios';
import {
    Container,
    Typography,
    Card,
    CardContent,
    TextField,
    Button,
    Box,
    Grid,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    List,
    ListItem,
    ListItemText,
    IconButton,
    Switch,
    FormControlLabel,
    Alert,
} from '@mui/material';
import { SmartToy, Add, Delete } from '@mui/icons-material';
import { getWhatsAppAccounts } from '../redux/slices/whatsappSlice';

export default function Chatbot() {
    const dispatch = useDispatch();
    const whatsappAccounts = useSelector((state) => state.whatsapp.accounts || []);

    const [rules, setRules] = useState([]);
    const [formData, setFormData] = useState({
        keyword: '',
        responseText: '',
        accountModel: 'WhatsappAccount',
        accountId: '',
    });

    useEffect(() => {
        fetchRules();
        dispatch(getWhatsAppAccounts());
    }, [dispatch]);

    const fetchRules = async () => {
        try {
            const token = localStorage.getItem('token');
            const { data } = await axios.get('https://social-auto-r8we.onrender.com/api/chatbot', {
                headers: { Authorization: `Bearer ${token}` },
            });
            setRules(data);
        } catch (error) {
            console.error(error);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (formData.accountModel === 'WhatsappAccount' && !formData.accountId) {
            alert('Please select a WhatsApp account');
            return;
        }

        try {
            const token = localStorage.getItem('token');
            await axios.post(
                'https://social-auto-r8we.onrender.com/api/chatbot',
                {
                    accountId: formData.accountId,
                    accountModel: formData.accountModel,
                    keyword: formData.keyword,
                    response: { text: formData.responseText },
                },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            fetchRules();
            setFormData({ keyword: '', responseText: '', accountModel: 'WhatsappAccount', accountId: '' });
        } catch (error) {
            alert(error.response?.data?.message || 'Error creating rule');
        }
    };

    const handleDelete = async (ruleId) => {
        if (!window.confirm('هل أنت متأكد من حذف هذه القاعدة؟')) {
            return;
        }

        try {
            const token = localStorage.getItem('token');
            await axios.delete(`https://social-auto-r8we.onrender.com/api/chatbot/${ruleId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            fetchRules();
        } catch (error) {
            alert(error.response?.data?.message || 'Error deleting rule');
        }
    };

    return (
        <Container maxWidth="lg" sx={{ py: 4 }}>
            <Typography variant="h4" sx={{ mb: 3, fontWeight: 700 }}>
                <SmartToy sx={{ mr: 1, verticalAlign: 'middle', color: '#00C853' }} />
                Chatbot Auto-Reply
            </Typography>

            <Grid container spacing={3}>
                {/* Create Rule */}
                <Grid item xs={12} md={5}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" sx={{ mb: 2 }}>Create Auto-Reply Rule</Typography>

                            <Box component="form" onSubmit={handleSubmit}>
                                <FormControl fullWidth sx={{ mb: 2 }}>
                                    <InputLabel>Platform</InputLabel>
                                    <Select
                                        value={formData.accountModel}
                                        onChange={(e) => setFormData({ ...formData, accountModel: e.target.value, accountId: '' })}
                                        label="Platform"
                                    >
                                        <MenuItem value="WhatsappAccount">WhatsApp</MenuItem>
                                        <MenuItem value="FacebookAccount">Facebook</MenuItem>
                                    </Select>
                                </FormControl>

                                {formData.accountModel === 'WhatsappAccount' && (
                                    <FormControl fullWidth sx={{ mb: 2 }}>
                                        <InputLabel>WhatsApp Account</InputLabel>
                                        <Select
                                            value={formData.accountId}
                                            onChange={(e) => setFormData({ ...formData, accountId: e.target.value })}
                                            label="WhatsApp Account"
                                            required
                                        >
                                            {whatsappAccounts.filter(acc => acc.status === 'connected').map((account) => (
                                                <MenuItem key={account._id} value={account._id}>
                                                    {account.number || account.phoneNumber || account._id}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                )}

                                <TextField
                                    fullWidth
                                    label="Keyword (Trigger)"
                                    value={formData.keyword}
                                    onChange={(e) => setFormData({ ...formData, keyword: e.target.value })}
                                    placeholder="hello"
                                    required
                                    sx={{ mb: 2 }}
                                />

                                <TextField
                                    fullWidth
                                    multiline
                                    rows={4}
                                    label="Auto-Reply Message"
                                    value={formData.responseText}
                                    onChange={(e) => setFormData({ ...formData, responseText: e.target.value })}
                                    placeholder="Thank you for your message!"
                                    required
                                    sx={{ mb: 2 }}
                                />

                                <Button
                                    type="submit"
                                    variant="contained"
                                    fullWidth
                                    size="large"
                                    startIcon={<Add />}
                                >
                                    Add Rule
                                </Button>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Rules List */}
                <Grid item xs={12} md={7}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" sx={{ mb: 2 }}>Active Rules</Typography>

                            {rules.length === 0 ? (
                                <Typography color="text.secondary">No rules configured yet</Typography>
                            ) : (
                                <List>
                                    {rules.map((rule) => (
                                        <ListItem
                                            key={rule._id}
                                            sx={{
                                                mb: 1,
                                                borderRadius: 2,
                                                border: '1px solid',
                                                borderColor: 'primary.main',
                                            }}
                                            secondaryAction={
                                                <IconButton
                                                    edge="end"
                                                    onClick={() => handleDelete(rule._id)}
                                                    color="error"
                                                >
                                                    <Delete />
                                                </IconButton>
                                            }
                                        >
                                            <ListItemText
                                                primary={
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <Typography variant="body1" fontWeight="600">
                                                            {rule.keyword}
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            ({rule.accountModel})
                                                        </Typography>
                                                    </Box>
                                                }
                                                secondary={rule.response.text}
                                            />
                                        </ListItem>
                                    ))}
                                </List>
                            )}
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
        </Container>
    );
}
